import { PaginationState, SortingState } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  EntitySearchConfigsToolbar,
  useEntitySearch,
} from 'src/components/EntitySearch';
import { EntitySearchRefetcherV2 } from 'src/components/EntitySearch/EntitySearchRefetcher/EntitySearchRefetcherV2';
import { SalesFlattenedView } from 'src/components/Events/EventListing/SalesEventListing/SalesFlattenedView';
import { SaleSearchBox } from 'src/components/Sales/SaleFilterBar/SaleSearchBox';
import { SearchInfoBanner } from 'src/components/SearchInfoBanner';
import { ActivePosEntityProvider } from 'src/contexts/ActivePosEntityContext';
import {
  ActiveSortTableColumnProvider,
  TableViewMode,
} from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import { CatalogDataContextProvider } from 'src/contexts/CatalogDataContext';
import { CatalogMultiSelectionContextProvider } from 'src/contexts/CatalogMultiSelectionContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { DialogProvider } from 'src/contexts/DialogContext/DialogContext';
import {
  FilterQueryContextProvider,
  useFilterQueryContext,
} from 'src/contexts/FilterQueryContext';
import {
  MultiSelectionContextProvider,
  MultiSelectScope,
  NO_GROUP_ID,
} from 'src/contexts/MultiSelectionContext';
import {
  SearchDataContextProvider,
  useSearchDataContext,
} from 'src/contexts/SearchDataContext';
import { StopwatchLoggingContextProvider } from 'src/contexts/StopwatchLoggingContext';
import { PosNoResultsFound } from 'src/core/POS/PosNoResultsFound';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { DividerLine } from 'src/core/ui';
import { LayoutContent } from 'src/navigations/LayoutContent';
import { createSaleSearchPageUrl } from 'src/utils/advancedSearchUtils';
import { SaleDeeplinkQueryParam } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { removeDeepLinkUrlPartsFromRelativeUrl } from 'src/utils/deepLinkUtils';
import {
  DefaultSaleQueryV2,
  EmptySaleQueryV2,
  mapSaleQueryToSaleQueryV2,
} from 'src/utils/eventQueryUtils';
import { transformData } from 'src/utils/eventWithDataUtils';
import { EventPageLocationState } from 'src/utils/types/EventPageLocationState';
import { SomethingWentWrong } from 'src/views';
import {
  ActionOutboxEntityType,
  SaleClient,
  SaleDetails,
  SaleQueryV2,
  SaleSearchResult,
  SalesViewMode,
  UISearchOptions,
} from 'src/WebApiController';

import { getDefaultReturnUrl } from '../InventoryEvent/constants';
import {
  ACTIVE_EVENT_IDS_2_QUERY_PARAM,
  ACTIVE_EVENT_IDS_QUERY_PARAM,
  TAB_EVENT_IDS_2_QUERY_PARAM,
  TAB_EVENT_IDS_QUERY_PARAM,
} from '../InventoryEvent/InventoryEvent.constants';
import { MainRoute } from '../MainRoute';
import {
  salesQueryValueTransformFromUrl,
  salesQueryValueTransformToUrl,
  searchSalesForEventsV2,
} from '../Sales/Sales';
import { SaleSearchFilterBar } from './SaleSearchFilterBar';
import { SaleSearchToolbar } from './SaleSearchToolbar';
import * as styles from './SaleSearchV2.css';

export function SaleSearchV2() {
  return (
    <FilterQueryContextProvider<SaleQueryV2>
      initialQuery={{
        ...DefaultSaleQueryV2,
        viewMode: SalesViewMode.FlattenedView,
      }}
      emptyQuery={EmptySaleQueryV2}
      queryValueTransformToUrl={salesQueryValueTransformToUrl}
      queryValueTransformFromUrl={salesQueryValueTransformFromUrl}
      saveQueryInUrl
    >
      <SaleSearchPage />
    </FilterQueryContextProvider>
  );
}

function SaleSearchPage() {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const [activeSearchConfig, setActiveSearchConfig] =
    useState<SaleSearchResult>();

  const getSearchData = useCallback(
    (ids: string[], f: SaleQueryV2) => {
      return searchSalesForEventsV2(ids, f, {
        activeAccountWebClientConfig,
        onError: (error) => {
          trackError('SaleClient.searchSalesForEvents', error, {
            ...f,
            eventIds: ids,
          });
        },
      });
    },
    [activeAccountWebClientConfig, trackError]
  );

  return (
    <StopwatchLoggingContextProvider>
      <CatalogDataContextProvider<SaleQueryV2>
        entityType={ActionOutboxEntityType.Sale}
        queryKey="getCatalogForSaleOrders"
        transformEventData={transformData}
        disableAutoRefresh
        ignoreMaxCount
        doNotLogPageLoad
      >
        <SearchDataContextProvider<SaleQueryV2>
          queryKey="getSearchDataForSaleOrders"
          entityType={ActionOutboxEntityType.Sale}
          getSearchData={getSearchData}
          disabled={
            activeSearchConfig == null // do not start query for items if there is no activeSearchConfig
          }
        >
          <CatalogMultiSelectionContextProvider type={'sale'}>
            <MultiSelectionContextProvider
              isFlattenedView
              forceSelectionMode={{
                mode: MultiSelectScope.AllGroups,
                groupId: NO_GROUP_ID,
              }}
              initialAllGroupsIds={[NO_GROUP_ID]}
            >
              <SaleSearchContent
                activeSearchConfig={activeSearchConfig}
                setActiveSearchConfig={setActiveSearchConfig}
              />
            </MultiSelectionContextProvider>
          </CatalogMultiSelectionContextProvider>
        </SearchDataContextProvider>
      </CatalogDataContextProvider>
    </StopwatchLoggingContextProvider>
  );
}

function SaleSearchContent({
  activeSearchConfig,
  setActiveSearchConfig,
}: {
  activeSearchConfig: SaleSearchResult | undefined;
  setActiveSearchConfig: (a: SaleSearchResult | undefined) => void;
}) {
  const searchTitle = useContent(ContentId.SearchSales);

  const { activeAccountWebClientConfig } = useAppContext();
  const location = useLocation();
  const navigate = useNavigate();

  const {
    errorInfo,
    isLoading: isSearchDataLoading,
    eventsTransformed,
    indexLastUpdatedAt,
  } = useSearchDataContext();

  const [pageIndex, setPageIndex] = useState(0);

  const {
    selectedFilters,
    setSelectedFilters,
    onSetActiveSearchConfig,
    onSaveSearchResult,
    onDeleteSearchResult,
    onRequeryForTheResult,
    refetchData,
    disabled,
    setDisabled,
  } = useEntitySearch(
    activeSearchConfig,
    setActiveSearchConfig,
    SalesViewMode.FlattenedView,
    (client, id) => client.getSaleSearchResult(id),
    (client, r) => client.saveSaleSearchResult(r)
  );

  const { filterQuery, setFilterQuery } = useFilterQueryContext<SaleQueryV2>();

  const { refetch: refetchSearchData, pitId } = useSearchDataContext();

  // Only use value provided at the first time (should be from '/purchases')
  const returnUrl = useMemo(() => {
    const locationState = (location.state ?? {}) as EventPageLocationState;

    if (locationState.returnUrl && locationState.keepReturnUrlUnchanged) {
      return locationState.returnUrl;
    }

    const relativeUrl =
      locationState.returnUrl || getDefaultReturnUrl('purchases');

    return removeDeepLinkUrlPartsFromRelativeUrl(relativeUrl, [
      SaleDeeplinkQueryParam,
      ACTIVE_EVENT_IDS_QUERY_PARAM,
      TAB_EVENT_IDS_QUERY_PARAM,
      ACTIVE_EVENT_IDS_2_QUERY_PARAM,
      TAB_EVENT_IDS_2_QUERY_PARAM,
    ]);
  }, [location.state]);

  const getActivePosEntity = useCallback(
    async (saleId: number) => {
      const saleDetails = await new SaleClient(
        activeAccountWebClientConfig
      ).getSaleBySaleId(saleId);
      if (saleDetails) {
        return {
          posEntityId: saleDetails.id,
          posEntity: saleDetails,
          event: null,
          posEntityDisplayId: saleDetails.id.toString(),
        };
      }

      return {};
    },
    [activeAccountWebClientConfig]
  );

  const dataIsLoading = isSearchDataLoading;

  const onRedirectToSearchV1 = useCallback(() => {
    const searchUrl = createSaleSearchPageUrl(false);

    // Having small delay so the click is a bit more natural
    setTimeout(() => {
      navigate(searchUrl, {
        state: {
          returnUrl: returnUrl,
        },
      });
    }, 10);
  }, [navigate, returnUrl]);

  const onPaginationChange = useCallback(
    (pagination: PaginationState) => {
      setPageIndex(pagination.pageIndex);
      setFilterQuery({
        ...filterQuery,
        searchOptions: {
          ...((filterQuery.searchOptions ?? {}) as UISearchOptions),
          skip: pagination.pageIndex * pagination.pageSize,
          take: pagination.pageSize,
          pitId: pitId ?? null,
          includeAggregations: true,
        },
      });
    },
    [filterQuery, pitId, setFilterQuery]
  );

  const onSortingChange = useCallback(
    (sorting: SortingState) => {
      setFilterQuery({
        ...filterQuery,
        searchOptions: {
          ...((filterQuery.searchOptions ?? {}) as UISearchOptions),
          pitId: pitId ?? null,
          includeAggregations: true,
          sort: sorting[0]
            ? [
                {
                  field: sorting[0].id,
                  desc: sorting[0].desc,
                },
              ]
            : null,
        },
      });
    },
    [filterQuery, pitId, setFilterQuery]
  );

  const onSetActiveSearchConfigWrapper = useCallback(
    (searchConfig?: SaleSearchResult | undefined) => {
      const mappedSearchConfig: SaleSearchResult | undefined = searchConfig
        ? {
            ...searchConfig,
            query: null,
            queryV2:
              (searchConfig.query ?? searchConfig.queryV2)
                ? mapSaleQueryToSaleQueryV2(
                    searchConfig.query ?? searchConfig.queryV2!
                  )
                : null,
          }
        : undefined;

      onSetActiveSearchConfig(mappedSearchConfig);
    },
    [onSetActiveSearchConfig]
  );

  return (
    <ActivePosEntityProvider<SaleDetails>
      entityType={ActionOutboxEntityType.Sale}
      getActivePosEntity={getActivePosEntity}
    >
      <ActiveSortTableColumnProvider
        entityType={ActionOutboxEntityType.Sale}
        viewMode={TableViewMode.SearchView}
        defaultSorting={[]}
      >
        <DialogProvider>
          <LayoutContent
            mainRoute={MainRoute.SalesSearchV2}
            routeTitle={searchTitle}
            returnUrl={returnUrl}
            rightContent={
              <EntitySearchRefetcherV2 lastRefreshedAt={indexLastUpdatedAt} />
            }
            centerContent={
              <SearchInfoBanner
                isOnSearchV2={true}
                onRedirectClick={onRedirectToSearchV1}
              />
            }
          >
            <EntitySearchConfigsToolbar
              disabled={disabled}
              setDisabled={setDisabled}
              activeSearchConfig={activeSearchConfig}
              setActiveSearchConfig={onSetActiveSearchConfigWrapper}
              saveActiveSearchConfig={onSaveSearchResult}
              deleteActiveSearchConfig={onDeleteSearchResult}
              getSearchConfigs={(client, includeAllUser) =>
                client.getSaleSearchConfigs(includeAllUser)
              }
              getSearchResult={(client, id) => client.getSaleSearchResult(id)}
              additionalActions={<SaleSearchBox />}
            />
            <DividerLine style={{ margin: 0 }} />
            <SaleSearchFilterBar
              activeSearchConfig={activeSearchConfig}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              onSubmitFilter={(filter) => {
                if (!activeSearchConfig) {
                  // Setting an empty search config so it starts to filter
                  onSetActiveSearchConfig({
                    id: null,
                    name: null,
                    query: null,
                    queryV2: filter,
                    ownerUserId: null,
                    count: null,
                    entityIds: null,
                    lastFetchDate: null,
                  });
                }
                setPageIndex(0);
                refetchData(filter, async () => refetchSearchData(), false);
              }}
            />
            <DividerLine
              style={{ margin: 0, borderColor: vars.color.borderLight }}
            />
            <SaleSearchToolbar
              disabled={disabled}
              activeSearchConfig={activeSearchConfig}
            />
            <DividerLine
              style={{ margin: 0, borderColor: vars.color.borderLight }}
            />
            <div className={styles.contentContainer}>
              {errorInfo && (
                <SomethingWentWrong
                  header={errorInfo.errorHeader}
                  message={
                    errorInfo.errorMessage ?? (
                      <Content id={ContentId.FailToLoadListContent} />
                    )
                  }
                />
              )}
              {activeSearchConfig == null ? (
                <PosNoResultsFound mainMessage={ContentId.SearchToSeeResults} />
              ) : dataIsLoading ? (
                <PosSpinner />
              ) : eventsTransformed?.length ? (
                <SalesFlattenedView
                  useTableV2
                  alwaysShowCheckbox
                  showSelectAllOnHeader
                  enableColumnFilters={true}
                  ignoreMaxCount
                  serverSidePagination
                  onPaginationChange={onPaginationChange}
                  onSortingChange={onSortingChange}
                  serverSidePageIndex={pageIndex}
                  isSearchV2
                />
              ) : (
                <PosNoResultsFound
                  detailMessage={ContentId.AddFilterToShowResult}
                />
              )}
            </div>
          </LayoutContent>
        </DialogProvider>
      </ActiveSortTableColumnProvider>
    </ActivePosEntityProvider>
  );
}
